import { Injectable } from "@angular/core";
import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { AppConfig } from "../../app.config";
import { BehaviorSubject, defer, firstValueFrom, lastValueFrom, Observable, of, Subject, Subscription, throwError } from "rxjs";
import { Media, MediaSearchFilters, MediaContent, MediaShort, Tag } from "../../models/Media";
import { catchError, finalize, map, switchMap, take, tap, throttleTime } from "rxjs/operators";
import { Utils } from '../../utils/utils'
import { LoggedUser } from "../../models/loggedUser.service";
import { ChunkedUploadService, UploadProgress, ChunkInfo } from "../../services/chunked-upload.service";
import { mseStreamingSupported } from "./mse-streaming-supported";

@Injectable({ providedIn: 'root' })
export class MediaService {

  apiVersion = `v${AppConfig.data['apiVersion']}`;
  service = AppConfig.data['services']['media'];
  endpoint = `${this.service}`
  simple = `${this.service}`
  tagEndpoint = `${AppConfig.data['services']['tag']}/${this.apiVersion}`
  getMediaEndpoint = `${AppConfig.data['services']['getMedia']}/${this.apiVersion}`
  uploadMediaEndpoint = `${AppConfig.data['services']['uploadMedia']}/${this.apiVersion}`
  authservice = `${AppConfig.data['services']['auth']}`;
  cmsEndpoint = `${AppConfig.data['services']['cms']}/${this.apiVersion}`;

  token = localStorage.getItem('accessToken');
  iamToken$ = new BehaviorSubject(null);
  loading = false;
  lock = false;

  invalid$ = new BehaviorSubject(true);
  createMediaBase64$: BehaviorSubject<string> = new BehaviorSubject('');
  createMedia: BehaviorSubject<Media> = new BehaviorSubject({
    base64_complete: null,
    description: null,
    expiration_date: null,
    expiration_hour: null,
    id: null,
    publishing_date: null,
    publishing_hour: null,
    tags: null,
    title: null,
    width: null,
    height: null,
    original_size: null,
    larger_size: false,
    max_size: null,
    mimeTypeWithCodecs: null,
  } as Media);
  maxFileSizeConfig: any;

  private httpClient: HttpClient;

  constructor(
    private http: HttpClient,
    private handler: HttpBackend,
    private utils: Utils,
    private _user: LoggedUser,
    private chunkedUploadService: ChunkedUploadService
  ) {
    this.httpClient = new HttpClient(handler);
    this.utils.setStorage('mdService', this.authservice)
  }

  setCreateMediaBase64(base64: string) {
    this.createMediaBase64$.next(base64)
    this.createMedia.next({ ...this.createMedia.value, base64_complete: base64 })
  }

  setInvalid(status) {
    this.invalid$.next(status)
  }

  getInvalid() {
    return this.invalid$.asObservable()
  }

  getTags(q: string) {
    return this.http.get<Tag[]>(`${this.tagEndpoint}/autosuggest`, { params: { q } })
  }

  searchMedia(params: {
    query?: string;
    format?: string;
    per_page?: string;
    sort?: string;
    end_date?: string;
    start_date?: string;
    size?: any;
  }) {
    return this.http.get<MediaContent>(`${this.getMediaEndpoint}/resource_files`, { params })
  }

  setCreateMedia(media: Media) {
    this.createMedia.next({ ...this.createMedia.value, ...media })
  }

  getCreateMediaBase64() {
    return this.createMediaBase64$.asObservable()
  }

  newCreateMedia() {
    this.createMedia.next({
      base64_complete: null,
      description: null,
      expiration_date: null,
      expiration_hour: null,
      id: null,
      publishing_date: null,
      tags: null,
      publishing_hour: null,
      title: null
    })
  }

  private mediaCache = new Map<string, any>();

  getMediaById(id: string) {
    if (id === null) {
      return null
    }

    // Always refetch: upload puts media in mediaCache without fileContent; Docling/Speech
    // fill contents later. Returning the upload cache hides "Conteúdo textual da mídia".
    return this.http.get(`${this.getMediaEndpoint}/getmedia/resource/${id}`).pipe(
      tap((media: any) => {
        this.mediaCache.set(id, media);
        this.createMedia.next({ ...media, size: `${media.size != null ? `${media.size} MB` : undefined}` });
      })
    );
  }

  getCreateMedia() {
    return this.createMedia.asObservable()
  }

  deleteMedias(medias: string[]) {
    return this.http.delete(`${this.uploadMediaEndpoint}/uploadmedia/${medias.join(',')}`).pipe(
      tap(() => medias.forEach(id => this.mediaCache.delete(id)))
    )
  }

  private updateMediaCache(media: any, fallbackId?: string): void {
    const cached = media?.resource_file ?? media;
    const id = cached?.id ?? fallbackId;
    if (id) {
      this.mediaCache.set(id, cached);
    }
  }

  saveMedia(media: Media) {
    if (media?.larger_size != undefined && media?.larger_size === true) {
      return throwError(() => new Error("File size limit"));
    }

    if (media.id) {
      return this.http.put(`${this.uploadMediaEndpoint}/uploadmedia/updateMedia/${media?.id}`, media).pipe(
        tap((response: any) => {
          const hasResponseBody = response && typeof response === 'object' && Object.keys(response).length > 0;
          this.updateMediaCache(hasResponseBody ? { ...media, ...response } : media, media.id);
          this.createMedia.next({} as Media);
        })
      )
    } else {
      return this.http.post(`${this.uploadMediaEndpoint}/uploadmedia/createMedia`, { ...media, filename: undefined, size: undefined, extension: undefined } as Media).pipe(
        map((createdMedia: any) => {
          this.updateMediaCache(createdMedia);
          this.createMedia.next({} as Media);
          return { resource_file: createdMedia };
        })
      )
    }
  }

  /**
   * Upload de mídia usando chunks (para arquivos grandes)
   */
  uploadMediaChunked(file: File, mediaData: Partial<Media>): Observable<UploadProgress> {
    const chunks = this.chunkedUploadService.prepareChunks(file);

    const options = {
      chunks,
      mediaData: {
        ...mediaData,
        filename: file.name,
        original_size: file.size,
        content_type: file.type,
        mime_codec: mediaData.mime_codec || file.type,
        larger_size: mediaData.larger_size || false,
        max_size: mediaData.max_size || 0
      },
      onProgress: (progress: UploadProgress) => {
        // Callback para progresso - pode ser usado para atualizar UI
        console.log('Upload progress:', progress.progressPercentage + '%');
      },
      onComplete: (result: any) => {
        if (mediaData?.id) {
          this.updateMediaCache({
            ...(this.mediaCache.get(mediaData.id) || {}),
            ...mediaData,
            filename: file.name,
            original_filename: file.name,
            original_size: file.size,
            content_type: file.type
          }, mediaData.id);
        } else if (result?.id || result?.resource_file?.id) {
          this.updateMediaCache(result);
        }
        this.createMedia.next({} as Media);
      },
      onError: (error: any) => {
        // Callback para erro
        console.error('Upload error:', error);
      }
    };

    return this.chunkedUploadService.uploadFile(file, options);
  }

  /**
   * Chunked upload for LMS SCORM zip packages. Uses a dedicated path so the
   * zip is not listed in the media gallery.
   */
  uploadLmsScormChunked(file: File, mediaData: Partial<Media>): Observable<UploadProgress> {
    const zipType = mediaData.content_type || file.type || 'application/zip';
    const chunks = this.chunkedUploadService.prepareChunks(file);

    return this.chunkedUploadService.uploadFile(file, {
      chunks,
      chunkPath: '/uploadmedia/lms-scorm/chunk',
      mediaData: {
        ...mediaData,
        filename: file.name,
        original_size: file.size,
        content_type: zipType,
        mime_codec: mediaData.mime_codec || zipType,
        larger_size: mediaData.larger_size || false,
        max_size: mediaData.max_size || 0
      },
      onError: (error: any) => {
        console.error('SCORM upload error:', error);
      }
    });
  }

  /**
   * Upload de múltiplas mídias usando chunks
   */
  uploadMultipleMediaChunked(files: File[], mediaDataArray: Partial<Media>[]): Observable<UploadProgress[]> {
    const uploads = files.map((file, index) => {
      const mediaData = mediaDataArray[index] || {};
      return this.uploadMediaChunked(file, mediaData);
    });

    return this.chunkedUploadService.uploadMultipleFiles(files);
  }

  /**
   * Determina se deve usar upload chunked baseado no tamanho do arquivo
   */
  shouldUseChunkedUpload(file: File): boolean {
    return this.chunkedUploadService.validateFileSize(file);
  }

  /**
   * Upload inteligente que escolhe entre tradicional e chunked
   */
  uploadMediaIntelligent(file: File, mediaData: Partial<Media>): Observable<any> {
    if (this.shouldUseChunkedUpload(file)) {
      // Upload chunked para arquivos grandes
      return this.uploadMediaChunked(file, mediaData);
    } else {
      // Upload tradicional para arquivos pequenos
      const media: Media = {
        ...mediaData,
        base64_complete: '', // Será preenchido pelo componente
        original_size: file.size,
        larger_size: false,
        filename: file.name,
        content_type: file.type
      } as Media;

      return this.saveMedia(media);
    }
  }

  /**
   * Cancela upload em andamento
   */
  cancelUpload(uploadId: string): Observable<any> {
    return this.chunkedUploadService.cancelUpload(uploadId);
  }

  /**
   * Retry de chunks falhados
   */
  retryFailedChunks(uploadId: string): Observable<UploadProgress> {
    return this.chunkedUploadService.retryFailedChunks(uploadId);
  }

  /**
   * Obtém progresso de upload
   */
  getUploadProgress(): Observable<Map<string, UploadProgress>> {
    return this.chunkedUploadService.getUploadProgress();
  }

  /**
   * Obtém progresso de upload específico
   */
  getUploadProgressById(uploadId: string): UploadProgress | undefined {
    return this.chunkedUploadService.getUploadProgressById(uploadId);
  }

  mediaExtension(type) {
    if (type.includes('pdf')) {
      return 'pdf'
    } else if (type.includes('sheet') || type.includes('excel') || type.includes('csv') || type.includes('xls') || type.includes('wps') || type.includes('xlsx')) {
      return 'xls'
    } else if (type.includes('text/plain') || type.includes('rtf') || type.includes('rtx')) {
      return 'txt'
    } else if (type.includes('audio')) {
      return 'audio'
    } else if (type.includes('word')) {
      return 'doc'
    } else if (type.includes('video')) {
      return 'video'
    } else if (type.includes('powerpoint') || type.includes('presentation')) {
      return 'ppt'
    } else {
      return 'other'
    }
  }

  editAcl(aclId: string, readGroups: string[]) {
    return this.http.put(`${this.uploadMediaEndpoint}/acls/${aclId}`, { readGroups })
  }

  createAcl(mediaId: string, readGroups: string[]) {
    return this.http.post(`${this.uploadMediaEndpoint}/acls`, { mediaId, readGroups })
  }

  getMedia(mediaId) {
    if (mediaId === null) {
      return null
    }

    // Same as getMediaById: do not short-circuit on upload cache without fileContent.
    return this.http.get(`${this.getMediaEndpoint}/getmedia/resource/${mediaId}`).pipe(
      tap(media => {
        this.mediaCache.set(mediaId, media);
      })
    );
  }

  getBinarie(mediaId?) {
    const headers = new HttpHeaders();
    headers.set('Authorization', `Bearer ${this.token}`)
    return this.http.get(`${this.getMediaEndpoint}/getmedia/view/${mediaId}`, { headers, responseType: 'blob' })
  }

  getBinariefromUrl(url?) {
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${this.token}`)
    return this.http.get(`${url}`, { headers, responseType: 'blob' })
  }

  // alterado pra privado pra evitar buscar o token muitas vezes. Utilize o metodo getIamToken()
  private getIam() {
    const headers = new HttpHeaders()
      .set('ignoreInterceptorRedirect', 'true')
    return this.http.get<Record<string, string>>(`${this.getMediaEndpoint}/oauth/iam`, { headers })
  }

  getIamToken(forceGetNewToken = false): Observable<string> {
    if ((this.loading || !!this.iamToken$.getValue()) && !forceGetNewToken) {
      return this.iamToken$.asObservable().pipe(take(1))
    }

    // const tokenPersistedOnLocalStorage = localStorage.getItem("googleToken")

    // if (!!tokenPersistedOnLocalStorage) return of(tokenPersistedOnLocalStorage);

    if (!this.lock) {
      this.lock = true;

      return defer(() => {
        this.loading = true;

        return this.getIam()
          .pipe(
            map(resp => resp?.token),
            tap(token => {
              this.iamToken$.next(token);

              localStorage.setItem("googleToken", token);
            }),
            finalize(() => {
              this.loading = false;
              this.lock = false;
            })
          )
      })
    } else {
      return this.iamToken$.asObservable().pipe(take(1));
    }
  }

  getCDNImage(url: string, iam: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${iam}`
    });

    return this.httpClient.get(`${url}`, { headers, responseType: 'blob' }).pipe(
      catchError((err) => {
        if (err?.status === 401) {
          // Se der 401, tenta pegar um novo token
          return this.getIamToken(!!iam).pipe(
            switchMap((newToken: string) => {
              // Após pegar o novo token, refaz a requisição com ele
              const newHeaders = new HttpHeaders({
                'Authorization': `Bearer ${newToken}`
              });
              return this.httpClient.get(`${url}`, { headers: newHeaders, responseType: 'blob' });
            })
          );
        }

        // Para outros erros, apenas retorna o erro
        return of({ error: err });
      })
    );
  }

  toDataURL(file: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        let result = reader.result as string
        let encoded = result.replace(/^data:(.*;base64,)?/, '');
        if ((encoded.length % 4) > 0) {
          encoded += '='.repeat(4 - (encoded.length % 4));
        }
        resolve({ result, encoded });
      };
      reader.onerror = error => reject(error);
    });
  };


  postMedia(data) {
    return this.http.post(`${this.uploadMediaEndpoint}/uploadmedia/socialnet`, data)
  }

  downloadMedia(mediaUrl: string, mediaName: string) {
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${this.iamToken$.getValue()}`)

    this.httpClient.get(mediaUrl, { responseType: 'blob', headers }).subscribe({
      next: (res) => {
        const url = window.URL.createObjectURL(res);
        const link = document.createElement('a');
        link.href = url;
        link.download = mediaName;
        link.click();
      }
      , error: (err) => {
        this.iamToken$.next(null);
        return of(null)
      }
    });
  }

  saveUserProfilePhoto(photo) {
    return this.http.post(`${this.uploadMediaEndpoint}/uploadmedia/photo`, photo)
  }

  saveUserCoverPhoto(photo) {
    return this.http.post(`${this.uploadMediaEndpoint}/uploadmedia/user-cover-image`, photo)
  }

  saveUserCurriculum(curriculum) {
    return this.http.post(`${this.uploadMediaEndpoint}/uploadmedia/user-curriculum`, curriculum)
  }

  getDownloadMediaLink(key: string) {
    return `${this.getMediaEndpoint}/getmedia/resource/url/${key}`
  }

  /**
   * Stream fMP4 via MSE. Retorna disposer para pausar e abortar fetch/append
   * (necessário ao sair da página — o <video> detached continua tocando sem isso).
   */
  getCDNVIdeo(media, page, token, video, seconds, error): () => void {
    console.log(media, page)
    const url = media?.url;
    const mimeCodec = media?.mimeCodec;
    const PROBE_SIZE = 2 * 1024 * 1024;
    const PREFETCH_FRAGMENTS = 6;

    let fileTotalLen = 0;
    let initEnd = 0;
    let videoTimescale = 30000;
    let fragments: { start: number; end: number; timeSec: number }[] = [];
    let indexBuilt = false;
    let indexBuilding: Promise<void> | null = null;
    let hasError = false;
    let disposed = false;
    let appending = false;
    const appendQueue: { chunk: ArrayBuffer; isFragment: boolean; resolve: () => void; reject: (e: any) => void }[] = [];
    let pendingAppend: { resolve: () => void; reject: (e: any) => void } | null = null;
    const appended = new Set<number>();
    let ensureInFlight = false;
    const activeXhrs = new Set<XMLHttpRequest>();

    const noopDispose = () => {};
    if (!mseStreamingSupported(mimeCodec)) {
      try { error(video?.currentTime); } catch (_) { }
      return noopDispose;
    }

    let mediaSource: MediaSource;
    try {
      mediaSource = new MediaSource();
    } catch (_) {
      try { error(video?.currentTime); } catch (__) { }
      return noopDispose;
    }
    let sourceBuffer = null;
    const checkpoints = new Set();
    let watchedTime = 0;
    let objectUrl: string | null = null;

    const dispose = () => {
      if (disposed) {
        return;
      }
      disposed = true;
      hasError = true;
      appendQueue.length = 0;
      activeXhrs.forEach((xhr) => {
        try { xhr.abort(); } catch (_) { }
      });
      activeXhrs.clear();
      try {
        video.pause();
      } catch (_) { }
      try {
        video.removeEventListener('error', onError);
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('seeking', onSeeking);
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('canplay', onCanPlay);
      } catch (_) { }
      try {
        if (sourceBuffer) {
          sourceBuffer.removeEventListener('updateend', onUpdateEnd);
        }
      } catch (_) { }
      try {
        if (mediaSource.readyState === 'open') {
          mediaSource.endOfStream();
        }
      } catch (_) { }
      try {
        video.removeAttribute('src');
        video.load();
      } catch (_) { }
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch (_) { }
        objectUrl = null;
      }
    };

    objectUrl = URL.createObjectURL(mediaSource);
    video.src = objectUrl;
    mediaSource.addEventListener('sourceopen', sourceOpen);
    video.addEventListener('error', onError);

    function fail(currentTime?, reason?: string) {
      if (hasError || disposed) {
        return;
      }
      hasError = true;
      error(currentTime, reason);
    }

    function onError(_) {
      console.log(video.error);
      if (hasError || disposed) {
        return;
      }
      fail(video?.currentTime, indexBuilt ? 'MSE_APPEND_FAILED' : undefined);
    }

    function onTimeUpdate() {
      if (disposed) {
        return;
      }
      trackWatchedTime(video);
      ensureAround(video.currentTime);
    }

    function onSeeking() {
      if (disposed) {
        return;
      }
      ensureAround(video.currentTime, true);
    }

    function onWaiting() {
      if (disposed) {
        return;
      }
      ensureAround(video.currentTime);
    }

    function onCanPlay() {
      if (disposed) {
        return;
      }
      video.play().catch(() => { });
    }

    function sourceOpen(_) {
      if (disposed) {
        return;
      }
      try {
        sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
        sourceBuffer.mode = 'segments';
      } catch (e) {
        console.warn('addSourceBuffer failed', e);
        fail();
        return;
      }
      sourceBuffer.addEventListener('updateend', onUpdateEnd);
      sourceBuffer.addEventListener('error', () => fail(video?.currentTime, indexBuilt ? 'MSE_APPEND_FAILED' : undefined));

      getFileLength(url).then(async (fileLength) => {
        if (disposed) {
          return;
        }
        fileTotalLen = Number(fileLength) || 0;
        console.log((fileTotalLen / 1024 / 1024).toFixed(2), 'MB', 'fMP4 box-aligned');

        video.addEventListener('timeupdate', onTimeUpdate);
        video.addEventListener('seeking', onSeeking);
        video.addEventListener('waiting', onWaiting);
        video.addEventListener('canplay', onCanPlay);

        try {
          if (!applyServerFragmentIndex(media)) {
            await buildIndex();
          }
          if (disposed) {
            return;
          }
          const init = await fetchRange(url, 0, initEnd - 1);
          if (disposed) {
            return;
          }
          await enqueueAppend(toArrayBuffer(init), false);

          if (Number.isFinite(video.duration) && video.duration > 0 && mediaSource.readyState === 'open') {
            try { mediaSource.duration = video.duration; } catch (_) { }
          } else if (fragments.length) {
            const last = fragments[fragments.length - 1].timeSec;
            if (last > 0 && mediaSource.readyState === 'open') {
              try { mediaSource.duration = last + 2; } catch (_) { }
            }
          }

          if (seconds && seconds > 0) {
            video.currentTime = seconds;
            await ensureAround(seconds, true);
          } else {
            await ensureAround(0, true);
          }
        } catch (e) {
          if (disposed) {
            return;
          }
          console.warn('fMP4 MSE bootstrap failed', e);
          const msg = e && (e as any).message ? String((e as any).message) : '';
          if (
            msg.includes('Detected unfragmented')
            || msg.includes('index stuck')
            || msg.includes('moov/init not found')
            || msg.includes('box too large')
          ) {
            fail(video?.currentTime, 'UNFRAGMENTED');
          } else if (indexBuilt) {
            fail(video?.currentTime, 'MSE_APPEND_FAILED');
          } else {
            // falha estrutural antes do índice — ainda merece refragmentação lazy
            fail(video?.currentTime, 'UNFRAGMENTED');
          }
        }
      }).catch((e) => {
        if (disposed) {
          return;
        }
        console.warn('getFileLength failed', e);
        fail();
      });
    }

    function trackWatchedTime(v) {
      const t = Math.floor(v.currentTime);
      if (!checkpoints.has(t)) {
        checkpoints.add(t);
        watchedTime++;
      }
    }

    function onUpdateEnd() {
      if (disposed) {
        return;
      }
      appending = false;
      if (pendingAppend) {
        const done = pendingAppend;
        pendingAppend = null;
        done.resolve();
      }
      pumpAppendQueue();
      if (
        indexBuilt &&
        appended.size >= fragments.length &&
        appendQueue.length === 0 &&
        !appending &&
        mediaSource.readyState === 'open'
      ) {
        try { mediaSource.endOfStream(); } catch (e) { console.warn('endOfStream', e); }
      }
    }

    function enqueueAppend(chunk: ArrayBuffer, isFragment: boolean): Promise<void> {
      return new Promise((resolve, reject) => {
        if (hasError || disposed || !chunk || chunk.byteLength === 0) {
          resolve();
          return;
        }
        appendQueue.push({ chunk, isFragment, resolve, reject });
        pumpAppendQueue();
      });
    }

    function pumpAppendQueue() {
      if (hasError || disposed || appending || !sourceBuffer || appendQueue.length === 0 || sourceBuffer.updating) {
        return;
      }
      appending = true;
      const item = appendQueue.shift();
      pendingAppend = { resolve: item.resolve, reject: item.reject };
      try {
        sourceBuffer.appendBuffer(item.chunk);
      } catch (err) {
        console.warn('appendBuffer failed', err);
        appending = false;
        pendingAppend = null;
        item.reject(err);
        fail(video?.currentTime, indexBuilt ? 'MSE_APPEND_FAILED' : undefined);
      }
    }

    function readBox(buf: Uint8Array, offset: number) {
      if (offset + 8 > buf.length) {
        return { need: offset + 8 };
      }
      let size = ((buf[offset] << 24) | (buf[offset + 1] << 16) | (buf[offset + 2] << 8) | buf[offset + 3]) >>> 0;
      const typ = String.fromCharCode(buf[offset + 4], buf[offset + 5], buf[offset + 6], buf[offset + 7]);
      let header = 8;
      if (size === 1) {
        if (offset + 16 > buf.length) {
          return { need: offset + 16 };
        }
        const high = ((buf[offset + 8] << 24) | (buf[offset + 9] << 16) | (buf[offset + 10] << 8) | buf[offset + 11]) >>> 0;
        const low = ((buf[offset + 12] << 24) | (buf[offset + 13] << 16) | (buf[offset + 14] << 8) | buf[offset + 15]) >>> 0;
        size = high * 4294967296 + low;
        header = 16;
      } else if (size === 0) {
        size = buf.length - offset;
      }
      if (size < header) {
        return { need: offset + header };
      }
      return { size, typ, header };
    }

    function findTfdtTimes(moofBuf: Uint8Array): number[] {
      const times: number[] = [];
      const walk = (start: number, end: number) => {
        let i = start;
        while (i + 8 <= end) {
          const box = readBox(moofBuf, i);
          if ((box as any).need || i + (box as any).size > end) break;
          const payload = i + (box as any).header;
          if ((box as any).typ === 'tfdt') {
            const version = moofBuf[payload];
            let base: number;
            if (version === 1) {
              const hi = ((moofBuf[payload + 4] << 24) | (moofBuf[payload + 5] << 16) | (moofBuf[payload + 6] << 8) | moofBuf[payload + 7]) >>> 0;
              const lo = ((moofBuf[payload + 8] << 24) | (moofBuf[payload + 9] << 16) | (moofBuf[payload + 10] << 8) | moofBuf[payload + 11]) >>> 0;
              base = hi * 4294967296 + lo;
            } else {
              base = ((moofBuf[payload + 4] << 24) | (moofBuf[payload + 5] << 16) | (moofBuf[payload + 6] << 8) | moofBuf[payload + 7]) >>> 0;
            }
            times.push(base);
          }
          if ((box as any).typ === 'traf' || (box as any).typ === 'moof') {
            walk(payload, i + (box as any).size);
          }
          i += (box as any).size;
        }
      };
      walk(0, moofBuf.length);
      return times;
    }

    function readMdhdTimescale(initBuf: Uint8Array): number {
      const walk = (start: number, end: number): number | null => {
        let i = start;
        while (i + 8 <= end) {
          const box = readBox(initBuf, i);
          if ((box as any).need || i + (box as any).size > end) break;
          const payload = i + (box as any).header;
          if ((box as any).typ === 'mdhd') {
            const version = initBuf[payload];
            if (version === 1) {
              return ((initBuf[payload + 20] << 24) | (initBuf[payload + 21] << 16) | (initBuf[payload + 22] << 8) | initBuf[payload + 23]) >>> 0;
            }
            return ((initBuf[payload + 12] << 24) | (initBuf[payload + 13] << 16) | (initBuf[payload + 14] << 8) | initBuf[payload + 15]) >>> 0;
          }
          if (['moov', 'trak', 'mdia', 'minf', 'stbl', 'mvex'].includes((box as any).typ)) {
            const found = walk(payload, i + (box as any).size);
            if (found) return found;
          }
          i += (box as any).size;
        }
        return null;
      };
      return walk(0, initBuf.length) || 30000;
    }

    function applyServerFragmentIndex(mediaObj: any): boolean {
      const idx = mediaObj?.fragmentIndex || mediaObj?.fragment_index;
      if (!idx || !idx.initEnd || !Array.isArray(idx.fragments) || !idx.fragments.length) {
        return false;
      }
      initEnd = Number(idx.initEnd);
      videoTimescale = Number(idx.timescale) || 30000;
      fragments = idx.fragments.map((f: any) => ({
        start: Number(f.start),
        end: Number(f.end),
        timeSec: Number(f.timeSec),
      })).filter((f) => f.end > f.start);
      if (!initEnd || !fragments.length) {
        return false;
      }
      indexBuilt = true;
      console.log('fMP4 index (server)', { initEnd, fragments: fragments.length, first: fragments[0]?.timeSec, last: fragments[fragments.length - 1]?.timeSec, videoTimescale });
      return true;
    }

    async function buildIndex() {
      if (indexBuilt) return;
      if (indexBuilding) return indexBuilding;
      indexBuilding = (async () => {
        const MAX_INDEX_BOX_BYTES = 32 * 1024 * 1024;
        let offset = 0;
        let pendingMoof: { start: number; timeSec: number } | null = null;
        let sawMoof = false;
        let localInitEnd = 0;

        async function ensureBytes(buf: Uint8Array, needAbsEnd: number, baseOffset: number): Promise<Uint8Array> {
          let out = buf;
          while (baseOffset + out.length < needAbsEnd && baseOffset + out.length < fileTotalLen) {
            const moreEnd = Math.min(
              Math.max(needAbsEnd, baseOffset + out.length + PROBE_SIZE),
              fileTotalLen
            ) - 1;
            const more = await fetchRange(url, baseOffset + out.length, moreEnd);
            const merged = new Uint8Array(out.length + more.length);
            merged.set(out);
            merged.set(more, out.length);
            out = merged;
          }
          return out;
        }

        while (offset < fileTotalLen) {
          const end = Math.min(offset + PROBE_SIZE, fileTotalLen) - 1;
          let buf = await fetchRange(url, offset, end);
          let i = 0;

          while (offset + i < fileTotalLen) {
            while (i + 16 > buf.length && offset + buf.length < fileTotalLen) {
              buf = await ensureBytes(buf, offset + i + 16, offset);
            }
            if (i + 8 > buf.length) break;

            const box: any = readBox(buf, i);
            if (box.need) break;

            // moov de progressive pode passar de 2MB — buscar box inteira (exceto mdat)
            if (box.typ !== 'mdat' && i + box.size > buf.length) {
              if (box.size > MAX_INDEX_BOX_BYTES) {
                throw new Error('box too large for index: ' + box.typ + ' ' + box.size);
              }
              buf = await ensureBytes(buf, offset + i + box.size, offset);
              if (i + box.size > buf.length) {
                break;
              }
            }

            if (i + box.size > buf.length && box.typ !== 'mdat') break;

            const absStart = offset + i;
            const absEnd = absStart + box.size;

            if (box.typ === 'moov') {
              localInitEnd = absEnd;
              if (localInitEnd - offset <= buf.length) {
                videoTimescale = readMdhdTimescale(buf.subarray(0, localInitEnd - offset));
              }
            } else if (box.typ === 'moof') {
              sawMoof = true;
              const moofBuf = buf.subarray(i, i + box.size);
              const tfdts = findTfdtTimes(moofBuf);
              const raw = tfdts.length ? Math.min(...tfdts) : 0;
              pendingMoof = { start: absStart, timeSec: raw / videoTimescale };
            } else if (box.typ === 'mdat') {
              if (pendingMoof) {
                fragments.push({ start: pendingMoof.start, end: absEnd, timeSec: pendingMoof.timeSec });
                pendingMoof = null;
              } else if (localInitEnd > 0 && !sawMoof) {
                throw new Error('Detected unfragmented');
              }
            }

            i += box.size;
            // Pular corpo do mdat: não expandir o buffer até i (evita baixar o arquivo inteiro)
            if (i >= buf.length) {
              break;
            }
          }

          if (i === 0) {
            throw new Error('index stuck at offset ' + offset);
          }
          offset += i;
        }

        if (!localInitEnd) {
          throw new Error('moov/init not found');
        }
        if (!sawMoof || !fragments.length) {
          throw new Error('Detected unfragmented');
        }
        initEnd = localInitEnd;
        indexBuilt = true;
        console.log('fMP4 index', { initEnd, fragments: fragments.length, first: fragments[0]?.timeSec, last: fragments[fragments.length - 1]?.timeSec, videoTimescale });
      })();
      return indexBuilding;
    }

    function fragmentForTime(t: number) {
      if (!fragments.length) return null;
      let lo = 0;
      let hi = fragments.length - 1;
      let ans = fragments[0];
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (fragments[mid].timeSec <= t) {
          ans = fragments[mid];
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return ans;
    }

    async function ensureAround(t: number, force = false) {
      if (hasError || disposed || !indexBuilt) return;
      if (ensureInFlight && !force) return;

      ensureInFlight = true;
      try {
        const target = fragmentForTime(Math.max(0, t || 0));
        if (!target) return;
        const idx = fragments.indexOf(target);
        const from = Math.max(0, idx);
        const to = Math.min(fragments.length, from + PREFETCH_FRAGMENTS);
        for (let i = from; i < to; i++) {
          if (disposed) return;
          const f = fragments[i];
          if (appended.has(f.start)) continue;
          const buf = await fetchRange(url, f.start, f.end - 1);
          if (disposed) return;
          appended.add(f.start);
          await enqueueAppend(toArrayBuffer(buf), true);
        }
      } catch (e) {
        if (disposed) return;
        console.warn('ensureAround failed', e);
        fail(video?.currentTime, indexBuilt ? 'MSE_APPEND_FAILED' : undefined);
      } finally {
        ensureInFlight = false;
      }
    }

    function toArrayBuffer(u8: Uint8Array): ArrayBuffer {
      return u8.slice().buffer;
    }

    function getFileLength(u: string): Promise<number> {
      return new Promise((resolve, reject) => {
        if (disposed) {
          reject(new Error('disposed'));
          return;
        }
        const xhr = new XMLHttpRequest;
        activeXhrs.add(xhr);
        xhr.open('get', u);
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        xhr.setRequestHeader('Range', 'bytes=0-10');
        xhr.onload = function () {
          activeXhrs.delete(xhr);
          if (disposed) {
            reject(new Error('disposed'));
            return;
          }
          const contentRange = xhr.getResponseHeader('content-range');
          if (contentRange && contentRange.includes('/')) {
            resolve(Number(contentRange.split('/')[1]));
          } else {
            reject(new Error('missing content-range'));
          }
        };
        xhr.onerror = () => {
          activeXhrs.delete(xhr);
          reject(new Error('getFileLength xhr error'));
        };
        xhr.onabort = () => {
          activeXhrs.delete(xhr);
          reject(new Error('disposed'));
        };
        xhr.send();
      });
    }

    function fetchRange(u: string, start: number, end: number): Promise<Uint8Array> {
      return new Promise((resolve, reject) => {
        if (disposed) {
          reject(new Error('disposed'));
          return;
        }
        const xhr = new XMLHttpRequest;
        activeXhrs.add(xhr);
        xhr.open('get', u);
        xhr.responseType = 'arraybuffer';
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        xhr.setRequestHeader('Range', 'bytes=' + start + '-' + end);
        xhr.onload = function () {
          activeXhrs.delete(xhr);
          if (disposed) {
            reject(new Error('disposed'));
            return;
          }
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('fetched bytes:', start, end);
            let u8 = new Uint8Array(xhr.response);
            const expected = end - start + 1;
            if (xhr.status === 200 && u8.byteLength > expected) {
              u8 = u8.subarray(start, end + 1);
            }
            resolve(u8);
          } else {
            reject(new Error('HTTP ' + xhr.status));
          }
        };
        xhr.onerror = () => {
          activeXhrs.delete(xhr);
          reject(new Error('fetchRange xhr error'));
        };
        xhr.onabort = () => {
          activeXhrs.delete(xhr);
          reject(new Error('disposed'));
        };
        xhr.send();
      });
    }

    return dispose;
  }

  requestRefragment(mediaId: string, reason: string) {
    return this.http.post<{ status: string; detail?: string }>(
      `${this.uploadMediaEndpoint}/uploadmedia/refragment`,
      { mediaId, reason }
    ).pipe(take(1));
  }

  getMaxSizeByFileType(type: string): number {
    if (this.maxFileSizeConfig?.items?.length > 0) {
      const typeConfig = this.maxFileSizeConfig.items.find(c => c.contentType === type)

      if (typeConfig != undefined) return typeConfig.maxSize;
    }

    return this.maxFileSizeConfig?.otherContentTypeMaxSize || 30;
  }

  validateMBsize(bytes: number, type: string) {

    let max_size: number = this.getMaxSizeByFileType(type);

    if (bytes === 0) return null;

    if (bytes >= 1048576) {
      bytes = parseFloat((bytes / 1048576).toFixed(2))
      if (bytes > max_size) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  getMaxFileSizeConfig() {
    return this.http.get<any>(`${this.uploadMediaEndpoint}/max-file-size-configs`)
  }

  loadMaxFileSizeConfig() {
    if (this.maxFileSizeConfig !== undefined) return

    this.getMaxFileSizeConfig().subscribe({
      next: (config) => this.maxFileSizeConfig = config
    })
  }

  saveImageFileContent(mediaId: string, fileContent: String) {
    return this.http.post<any>(`${this.uploadMediaEndpoint}/uploadmedia/save-image-file-content`, { mediaId, fileContent })
  }

  sendVideoTracking(payload: {
    mediaId: string | null;
    mediaTitle: string | null;
    pageId: string | null;
    homePageId: string | null;
    menuId: string | null;
    pageUrl: string;
    isHomepage: boolean;
    playTimestamp: string;
    durationWatched: number;
  }) {
    this.http
      .post(`${this.cmsEndpoint}/video-tracking`, payload)
      .pipe(take(1))
      .subscribe({ error: (err) => console.warn('[VideoTracking] Falha ao enviar dados:', err) });
  }
}
